/**
 * Claim-age helpers — colour claims by how old they are relative to the
 * average extraction age of the invoices currently in view.
 *
 *   newer than average → green
 *   around average     → amber
 *   older than average → red
 */

export const parseExtractionTime = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/** Mean age (ms) of every claim with a valid extraction timestamp. */
export const computeAverageAgeMs = (timestamps, now = Date.now()) => {
  const ages = [];
  (timestamps || []).forEach((ts) => {
    const parsed = typeof ts === "number" ? ts : parseExtractionTime(ts);
    if (parsed == null) return;
    ages.push(Math.max(0, now - parsed));
  });
  if (!ages.length) return null;
  return ages.reduce((sum, age) => sum + age, 0) / ages.length;
};

/**
 * Map age/average ratio → HSL colour.
 * ratio 0 → green (#00d19e-ish), 1 → amber, 2+ → red (#ff3131-ish).
 * Hue walks green → yellow → orange → red (145 → 0).
 */
export const ageRatioToColor = (ratio) => {
  const t = Math.min(Math.max(Number(ratio) || 0, 0) / 2, 1);
  const hue = 145 * (1 - t);
  const sat = 78 + t * 10;
  const light = 40 + t * 4;
  return {
    hue,
    solid: `hsl(${hue.toFixed(1)} ${sat}% ${light}%)`,
    soft: `hsl(${hue.toFixed(1)} ${Math.min(sat + 8, 90)}% 94%)`,
    border: `hsl(${hue.toFixed(1)} ${sat}% ${Math.max(light - 8, 28)}%)`,
    heat: Math.round(t * 100),
  };
};

export const ageTier = (ratio) => {
  if (ratio < 0.6) return "fresh";
  if (ratio < 1.2) return "typical";
  if (ratio < 1.8) return "aging";
  return "stale";
};

export const getAgeMetrics = (extractedAt, avgAgeMs, now = Date.now()) => {
  const ts = parseExtractionTime(extractedAt);
  if (ts == null) return null;
  const ageMs = Math.max(0, now - ts);
  const ratio = avgAgeMs && avgAgeMs > 0 ? ageMs / avgAgeMs : 1;
  const colors = ageRatioToColor(ratio);
  return {
    ts,
    ageMs,
    avgAgeMs: avgAgeMs || ageMs,
    ratio,
    tier: ageTier(ratio),
    ...colors,
  };
};

/** Compact relative age for UI chips ("3d", "12h", "2mo"). */
export const formatCompactAge = (ageMs, locale = "en") => {
  const abs = Math.max(0, ageMs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const month = 30 * day;

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "always", style: "narrow" });

  if (abs < hour) {
    const m = Math.max(1, Math.floor(abs / minute));
    return rtf.format(-m, "minute");
  }
  if (abs < day) {
    return rtf.format(-Math.floor(abs / hour), "hour");
  }
  if (abs < month) {
    return rtf.format(-Math.floor(abs / day), "day");
  }
  return rtf.format(-Math.floor(abs / month), "month");
};

/** Longer relative phrase for tooltips ("3 days ago"). */
export const formatLongAge = (ageMs, locale = "en") => {
  const abs = Math.max(0, ageMs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const month = 30 * day;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (abs < hour) {
    return rtf.format(-Math.max(1, Math.floor(abs / minute)), "minute");
  }
  if (abs < day) {
    return rtf.format(-Math.floor(abs / hour), "hour");
  }
  if (abs < month) {
    return rtf.format(-Math.floor(abs / day), "day");
  }
  return rtf.format(-Math.floor(abs / month), "month");
};
